/**
 * @fileoverview The turn and crop a gardener is making to a picture, before it is saved.
 *
 * Shared by the three places a picture can be edited — a photo's own editor
 * (EditPhotoDialog), a photo waiting in Add photos and a bed's cover (both
 * through ImageEditDialog) — so the buttons behave the same in each. Nothing
 * here talks to the server: the caller sends `edit` when the gardener saves.
 *
 * Crop is on while there is a box: pressing Crop draws one (Free, a little in
 * from the edges), pressing it again takes it away. Square makes the box
 * square about its centre and keeps it so while it is dragged.
 *
 * Where: EditPhotoDialog and ImageEditDialog.
 * API: none.
 * Settings: none.
 * Words (en/es): none.
 *
 * @module useImageEdit
 */

import { useCallback, useMemo, useState } from 'react';
import {
  NO_EDIT, isNoEdit, squareUp, startCrop, turnEdit, turnedAspect,
  type CropBox, type CropShape, type ImageEdit,
} from '@/shared/lib/imageEdit';

/** The edit being made, and everything the editor's buttons do to it. */
export interface ImageEditState {
  /** The turn and crop so far. */
  edit: ImageEdit;
  /** Whether the edit would change the picture at all. */
  changed: boolean;
  /** The crop box's shape. */
  shape: CropShape;
  /** The turned picture's width over its height (1 until it has loaded). */
  aspect: number;
  /** Tells the editor the picture's own size, once it has loaded. */
  setNatural: (width: number, height: number) => void;
  /** A quarter turn, the crop box turning with it. */
  turn: (direction: 'left' | 'right') => void;
  /** Draws a crop box, or takes it away. */
  toggleCrop: () => void;
  /** Square or Free; Square squares the box there is. */
  setShape: (shape: CropShape) => void;
  /** Moves or resizes the crop box (the stage's drag). */
  setCrop: (crop: CropBox) => void;
  /** Forgets the edit: after it is saved, or a reset, or a new picture. */
  clear: () => void;
}

/**
 * A turn and crop in progress, for one picture at a time.
 *
 * @returns The edit and its verbs (kept the same between renders where they
 *   can be, so the stage's effects stay quiet; toggleCrop and setShape change
 *   with the picture's shape).
 */
export function useImageEdit(): ImageEditState {
  const [edit, setEdit] = useState<ImageEdit>(NO_EDIT);
  const [shape, setShapeState] = useState<CropShape>('free');
  const [natural, setNaturalState] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const aspect = turnedAspect(natural.width, natural.height, edit.rotate);

  /** ImageEditState.setNatural; the same size twice changes nothing, so no render loop. */
  const setNatural = useCallback((width: number, height: number) => {
    setNaturalState((current) => (current.width === width && current.height === height ? current : { width, height }));
  }, []);

  /** ImageEditState.turn. */
  const turn = useCallback((direction: 'left' | 'right') => setEdit((current) => turnEdit(current, direction)), []);

  /** ImageEditState.toggleCrop: a new box takes the shape chosen last. */
  const toggleCrop = useCallback(() => {
    setEdit((current) => (current.crop ? { ...current, crop: null } : { ...current, crop: startCrop(shape, aspect) }));
  }, [shape, aspect]);

  /** ImageEditState.setShape. */
  const setShape = useCallback((next: CropShape) => {
    setShapeState(next);
    if (next === 'square') setEdit((current) => (current.crop ? { ...current, crop: squareUp(current.crop, aspect) } : current));
  }, [aspect]);

  /** ImageEditState.setCrop. */
  const setCrop = useCallback((crop: CropBox) => setEdit((current) => ({ ...current, crop })), []);

  /** ImageEditState.clear: the shape goes back to Free too. */
  const clear = useCallback(() => {
    setEdit(NO_EDIT);
    setShapeState('free');
  }, []);

  return useMemo(() => ({
    edit, changed: !isNoEdit(edit), shape, aspect, setNatural, turn, toggleCrop, setShape, setCrop, clear,
  }), [edit, shape, aspect, setNatural, turn, toggleCrop, setShape, setCrop, clear]);
}
